import { useEffect, useState } from 'react';
import { useRPC2Call } from '@/contexts/RPC2Context';
import {
  fetchHomePingRecords,
  fetchPingRecords,
  HOME_PING_TASK_DISPLAY_NAME,
  PING_RECORDS_AUTO_REFRESH_MS,
  type PingRecord,
  type PingRecordsResponse,
  type PingTaskInfo,
} from '@/lib/pingRecords';

export interface PingHistoryPoint {
  time: string;
  latency: number | null;
  loss: number | null;
}

export interface PingStats {
  avgLatency: number;
  avgLoss: number;
  avgVolatility: number;
  history: PingHistoryPoint[];
  tasks: PingTaskStats[];
  hasData: boolean;
  isLoaded: boolean;
  isLoading: boolean;
}

export interface PingTaskStats {
  id: number;
  name: string;
  avgLatency: number;
  avgLoss: number;
  avgVolatility: number;
  history: PingHistoryPoint[];
  hasData: boolean;
  hasLatencyData: boolean;
}

const HISTORY_BUCKET_COUNT = 12;

function createEmptyStats({
  isLoaded = false,
  isLoading = false,
}: { isLoaded?: boolean; isLoading?: boolean } = {}): PingStats {
  return {
    avgLatency: 0,
    avgLoss: 0,
    avgVolatility: 0,
    history: [],
    tasks: [],
    hasData: false,
    isLoaded,
    isLoading,
  };
}

function getTaskLatency(task: PingTaskInfo): number | null {
  const value = task.avg ?? task.latest ?? task.value ?? task.p50;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function buildTaskStats(task: PingTaskInfo, records: PingRecord[]): PingTaskStats {
  const taskRecords = records.filter((record) => record.task_id === task.id);
  const history = buildPingHistory(taskRecords);
  const taskLatency = getTaskLatency(task);
  const historyLatencyValues = history
    .map((point) => point.latency)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const avgLatency = taskLatency ?? (
    historyLatencyValues.length > 0
      ? historyLatencyValues.reduce((sum, value) => sum + value, 0) / historyLatencyValues.length
      : 0
  );
  const historyLossValues = history
    .map((point) => point.loss)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  const avgLoss = typeof task.loss === 'number' && Number.isFinite(task.loss)
    ? task.loss
    : historyLossValues.length > 0
      ? historyLossValues.reduce((sum, value) => sum + value, 0) / historyLossValues.length
      : 0;
  const avgVolatility =
    typeof task.p99_p50_ratio === 'number' && Number.isFinite(task.p99_p50_ratio)
      ? task.p99_p50_ratio
      : 0;

  return {
    id: task.id,
    name: task.name,
    avgLatency,
    avgLoss,
    avgVolatility,
    history,
    hasLatencyData: taskLatency !== null || historyLatencyValues.length > 0,
    hasData: taskLatency !== null || historyLatencyValues.length > 0 || historyLossValues.length > 0,
  };
}

function buildPingHistory(records: PingRecord[]): PingHistoryPoint[] {
  const sortedRecords = records
    .map((record) => {
      const parsed = new Date(record.time).getTime();
      const timestamp = Number.isFinite(parsed)
        ? parsed
        : Date.parse(record.time.replace(" ", "T") + (record.time.endsWith("Z") ? "" : "Z"));
      return {
        ...record,
        timestamp,
      };
    })
    .filter((record) => Number.isFinite(record.timestamp))
    .sort((left, right) => left.timestamp - right.timestamp);

  if (sortedRecords.length === 0) {
    return [];
  }

  const firstTime = sortedRecords[0].timestamp;
  const lastTime = sortedRecords[sortedRecords.length - 1].timestamp;
  const bucketCount = Math.min(HISTORY_BUCKET_COUNT, sortedRecords.length);
  const bucketSize = Math.max(1, (lastTime - firstTime) / bucketCount);

  return Array.from({ length: bucketCount }, (_, index) => {
    const startTime = firstTime + bucketSize * index;
    const endTime = index === bucketCount - 1 ? lastTime + 1 : startTime + bucketSize;
    const bucketRecords = sortedRecords.filter(
      (record) => record.timestamp >= startTime && record.timestamp < endTime,
    );
    const validLatencyRecords = bucketRecords.filter((record) => record.value >= 0);
    const lostRecords = bucketRecords.length - validLatencyRecords.length;
    const latency =
      validLatencyRecords.length > 0
        ? validLatencyRecords.reduce((sum, record) => sum + record.value, 0) / validLatencyRecords.length
        : null;
    const loss = bucketRecords.length > 0 ? (lostRecords / bucketRecords.length) * 100 : null;

    return {
      time: new Date(startTime).toISOString(),
      latency,
      loss,
    };
  });
}

export function usePingStats(
  uuid: string,
  hours: number = 24,
  enabled: boolean = true,
  homePingIpv4: string = "",
): PingStats {
  const { call } = useRPC2Call();
  const [stats, setStats] = useState<PingStats>(() => createEmptyStats());

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!uuid.trim()) {
      setStats(createEmptyStats({ isLoaded: true }));
      return;
    }

    let active = true;

    let hasReceivedResponse = false;

    const fetchStats = async (forceRefresh = false) => {
      try {
        if (!hasReceivedResponse) {
          setStats((previous) => ({ ...previous, isLoading: true }));
        }

        const emptyHome: PingRecordsResponse = { count: 0, records: [], tasks: [] };
        const [nodeResult, homeResult] = await Promise.all([
          fetchPingRecords(call, uuid, hours, { forceRefresh }),
          fetchHomePingRecords(call, uuid, homePingIpv4, hours, { forceRefresh }).catch(() => emptyHome),
        ]);
        if (!active) return;

        hasReceivedResponse = true;
        const records = [
          ...(nodeResult?.records || []),
          ...(homeResult?.records || []),
        ];
        const tasksById = new Map<number, PingTaskInfo>();
        [...(nodeResult?.tasks || []), ...(homeResult?.tasks || [])].forEach((task) => {
          tasksById.set(task.id, task);
        });
        const tasks = Array.from(tasksById.values()).sort((left, right) => {
          const leftHome = left.name.trim() === HOME_PING_TASK_DISPLAY_NAME
            || left.name.trim().startsWith(`${HOME_PING_TASK_DISPLAY_NAME} `);
          const rightHome = right.name.trim() === HOME_PING_TASK_DISPLAY_NAME
            || right.name.trim().startsWith(`${HOME_PING_TASK_DISPLAY_NAME} `);
          if (leftHome !== rightHome) return leftHome ? 1 : -1;
          return 0;
        });

        if (tasks.length === 0) {
          setStats(createEmptyStats({ isLoaded: true }));
          return;
        }

        const taskStats = tasks.map((task) => buildTaskStats(task, records));
        const history = buildPingHistory(records);
        const latencyValues = taskStats
          .filter((task) => task.hasData)
          .map((task) => task.avgLatency);
        const avgLatency = latencyValues.length > 0
          ? latencyValues.reduce((sum, value) => sum + value, 0) / latencyValues.length
          : 0;

        const totalLoss = taskStats.reduce((sum, task) => sum + task.avgLoss, 0);
        const avgLoss = taskStats.length > 0 ? totalLoss / taskStats.length : 0;

        const volatilityValues = taskStats
          .map((task) => task.avgVolatility)
          .filter((value) => value > 0);

        const avgVolatility = volatilityValues.length > 0
          ? volatilityValues.reduce((sum, val) => sum + val, 0) / volatilityValues.length
          : 0;

        setStats({
          avgLatency,
          avgLoss,
          avgVolatility,
          history,
          tasks: taskStats,
          hasData: taskStats.some((task) => task.hasData),
          isLoaded: true,
          isLoading: false,
        });
      } catch {
        if (!active) return;
        if (!hasReceivedResponse) {
          setStats(createEmptyStats({ isLoaded: true }));
        }
      }
    };

    void fetchStats();
    const refreshTimer = window.setInterval(() => {
      void fetchStats(true);
    }, PING_RECORDS_AUTO_REFRESH_MS);

    return () => {
      active = false;
      window.clearInterval(refreshTimer);
    };
  }, [uuid, hours, enabled, homePingIpv4, call]);

  return stats;
}
